import { useTranslation } from "react-i18next";
import { useStore } from "../../../store/store";
import Tab from "./Tab";
import { useEffect, useMemo } from "react";
import { TabId } from "../../store/types";
interface IProps {
	chat: boolean | undefined;
	canComment: boolean | undefined;
}

interface TabItem {
	title: string;
	number: TabId;
	badge?: number;
}

const Tabs = (props: IProps) => {
	const liveDetails = useStore((state) => state.live);
	const activeTab = useStore((state) => state.activeTab);
	const setActiveTab = useStore((state) => state.setActiveTab);
	const publicPolls = useStore((state) => state.publicPolls);
	const publicLiveFiles = useStore((state) => state.publicLiveFiles);
	const messagesNotifications = useStore((state) => state.messagesNotifications);
	const approvedQuestionsNotifications = useStore((state) => state.approvedQuestionsNotifications);
	const { t } = useTranslation();
	const user = useStore((state) => state.user);

	const hasQuestions = (user?.role && user?.role !== "listener") || props.canComment;
	const hasChat = props.chat;
	const hasShareFiles = liveDetails?.canShareFile;
	// Documents tab only if GET /live/file-live/:id returned at least one public file, or socket added to store.
	const hasLiveDocuments = publicLiveFiles.length > 0;
	const hasPolls = liveDetails?.poll;

	const allTabs = useMemo(() => {
		const tabs: TabItem[] = [];
		if (hasQuestions) {
			tabs.push({ title: t("live_page.questions"), number: 2, badge: approvedQuestionsNotifications });
		}
		if (hasChat) {
			tabs.push({ title: t("live_page.chat"), number: 1, badge: messagesNotifications });
		}
		if (hasPolls) {
			tabs.push({ title: t("live_page.polls"), number: 9, badge: publicPolls.length });
		}
		if (hasLiveDocuments) {
			tabs.push({
				title: t("live_page.live_documents"),
				number: 10,
				badge: publicLiveFiles.length,
			});
		}
		if (hasShareFiles) {
			tabs.push({ title: t("live_page.advanced_settings"), number: 8 });
		}
		// Details last; all tab labels are shown in one row (equal flex width, text truncates on tiny screens).
		tabs.push({ title: t("live_page.details"), number: 3 });
		return tabs;
	}, [
		hasQuestions,
		hasChat,
		hasPolls,
		hasShareFiles,
		hasLiveDocuments,
		publicPolls.length,
		publicLiveFiles.length,
		messagesNotifications,
		approvedQuestionsNotifications,
		t,
	]);

	useEffect(() => {
		if (hasQuestions) {
			setActiveTab(2);
			return;
		}
		if (hasChat) {
			setActiveTab(1);
			return;
		}
		if (hasShareFiles) {
			setActiveTab(8);
			return;
		}
		setActiveTab(3);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		props.canComment,
		props.chat,
		user?.role,
		liveDetails?.canShareFile,
	]);

	/** If Documents tab is removed (no files left), leave tab 10 so the panel does not render empty. */
	useEffect(() => {
		if (activeTab === 10 && publicLiveFiles.length === 0) {
			setActiveTab(3);
		}
	}, [activeTab, publicLiveFiles.length, setActiveTab]);

	return (
		<div className="w-full min-w-0 max-w-full">
			{activeTab === 4 ? (
				<ul className="relative flex h-full w-full items-center font-semibold text-primary">
					<Tab title={t("live_page.questions")} number={4} />
				</ul>
			) : (
				<div className="w-full min-w-0 max-w-full">
					<ul className="flex h-full min-h-[3.25rem] w-full min-w-0 flex-nowrap items-stretch font-semibold text-primary">
						{allTabs.map((tab) => (
							<Tab
								key={tab.number}
								title={tab.title}
								number={tab.number}
								badge={tab.badge}
							/>
						))}
					</ul>
				</div>
			)}
		</div>
	);
};

export default Tabs;
